import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
/**
 * @todo: experimental. Pros: unified way of getting and casting params. Cons: not sure if it's needed.
 * @todo: Candidate for including to a shared library.
 *
 * Retrieves route parameters and allows them to be cast using a user-defined caster function.
 *
 * @template CasterOrCasterMap
 * @typedef {(value: any) => any} Caster
 * @typedef {Record<string, Caster>} CasterMap
 *
 * This type parameter can either be a single caster function or a mapping of parameters to caster functions.
 *
 * If a single caster function is provided, all parameters will be cast using this function.
 * This "shortcut" is useful when you want to retrieve a single parameter or
 * when all parameters should be cast using the same function.
 *
 * @example
 * ```tsx
 * // For a route like /projects/:projectId
 * const { projectId } = useCastedParams(Number); // Casts projectId to a number
 * console.log(projectId); // Logs the projectId as a number
 * ```
 *
 * @example
 * ```tsx
 * // For a route like /projects/:projectId/contributors/:contributorId
 * const params = useCastedParams(Number); // Casts both projectId and contributorId to numbers
 * const { projectId, contributorId } = params;
 * console.log(projectId, contributorId); // Logs both the projectId and contributorId as numbers
 * ```
 *
 * If a mapping is provided, only the parameters that exist in this mapping
 * will be cast using their corresponding functions.
 * Parameters that are not included in the mapping will not be processed.
 */
const useCastedParams = (casterOrCasterMap) => {
    const params = useParams();
    // TODO: Should return type be wrapped with Partial<>?
    // TODO: Should return type be wrapped with Readonly<>?
    return useMemo(() => Object.entries(params).reduce((acc, [key, value]) => {
        const caster = typeof casterOrCasterMap === 'function'
            ? casterOrCasterMap
            : key in casterOrCasterMap
                ? casterOrCasterMap[key]
                : (value) => value;
        return { ...acc, [key]: caster(value) };
    }, {}), [params]);
};
export default useCastedParams;
